import { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import Modal from '../Modal';

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' }
];

const COLOR_OPTIONS = [
  '#C45D3A', // Terracotta (default)
  '#5B6BD4', // Blue
  '#2D8A5F', // Green
  '#8B5CF6', // Purple
  '#E67E22', // Orange
  '#6B7280'  // Gray
];

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export default function MeetingTemplateModal({
  isOpen,
  onClose,
  onSave,
  editItem
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    defaultDurationMinutes: 60,
    color: '#C45D3A',
    defaultAgendaItems: []
  });
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens/closes or edit item changes
  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        setFormData({
          name: editItem.name || '',
          description: editItem.description || '',
          defaultDurationMinutes: editItem.defaultDurationMinutes || 60,
          color: editItem.color || '#C45D3A',
          defaultAgendaItems: editItem.defaultAgendaItems || []
        });
      } else {
        setFormData({
          name: '',
          description: '',
          defaultDurationMinutes: 60,
          color: '#C45D3A',
          defaultAgendaItems: []
        });
      }
    }
  }, [isOpen, editItem]);

  // Handle agenda item changes
  const handleAddAgendaItem = () => {
    setFormData(prev => ({
      ...prev,
      defaultAgendaItems: [
        ...prev.defaultAgendaItems,
        {
          id: generateUUID(),
          title: '',
          description: '',
          durationMinutes: 10,
          order: prev.defaultAgendaItems.length
        }
      ]
    }));
  };

  const handleUpdateAgendaItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      defaultAgendaItems: prev.defaultAgendaItems.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleRemoveAgendaItem = (id) => {
    setFormData(prev => ({
      ...prev,
      defaultAgendaItems: prev.defaultAgendaItems.filter(item => item.id !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editItem ? 'Edit Template' : 'New Meeting Template'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5">
            Template Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Leadership Meeting"
            className="input-premium w-full"
            autoFocus
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5">
            Description (optional)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of this meeting type"
            className="input-premium w-full resize-none"
            rows={2}
          />
        </div>

        {/* Duration and Color row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Default Duration
            </label>
            <select
              value={formData.defaultDurationMinutes}
              onChange={(e) => setFormData(prev => ({ ...prev, defaultDurationMinutes: Number(e.target.value) }))}
              className="input-premium w-full"
            >
              {DURATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Color
            </label>
            <div className="flex gap-2 py-1">
              {COLOR_OPTIONS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, color }))}
                  className={`w-6 h-6 rounded-full transition-all ${
                    formData.color === color ? 'ring-2 ring-offset-2 ring-accent' : ''
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Default Agenda Items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-text-secondary">
              Default Agenda Items
            </label>
            <button
              type="button"
              onClick={handleAddAgendaItem}
              className="flex items-center gap-1 text-xs text-accent hover:text-accent-bright transition-colors"
            >
              <Plus size={12} />
              Add Item
            </button>
          </div>
          <div className="space-y-2">
            {formData.defaultAgendaItems.length === 0 ? (
              <p className="text-xs text-text-tertiary italic py-2">
                Add agenda items that will be included by default when using this template.
              </p>
            ) : (
              formData.defaultAgendaItems.map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-start gap-2 p-2 bg-surface-hover rounded-lg"
                >
                  <GripVertical size={14} className="text-text-muted mt-2 flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => handleUpdateAgendaItem(item.id, 'title', e.target.value)}
                      placeholder="Agenda item title"
                      className="input-premium w-full text-sm"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={item.description || ''}
                        onChange={(e) => handleUpdateAgendaItem(item.id, 'description', e.target.value)}
                        placeholder="Description (optional)"
                        className="input-premium flex-1 text-xs"
                      />
                      <select
                        value={item.durationMinutes || 10}
                        onChange={(e) => handleUpdateAgendaItem(item.id, 'durationMinutes', Number(e.target.value))}
                        className="input-premium w-20 text-xs"
                      >
                        <option value={5}>5 min</option>
                        <option value={10}>10 min</option>
                        <option value={15}>15 min</option>
                        <option value={20}>20 min</option>
                        <option value={30}>30 min</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAgendaItem(item.id)}
                    className="p-1 text-text-muted hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-text-secondary bg-surface-hover hover:bg-surface-active rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!formData.name.trim() || saving}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-accent hover:bg-accent-bright rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
